<template>
  <label
    v-if="dsComponentDetails.version === VersionOptions.LEGACY"
    :for="internalId"
    :class="radioControlClass"
    :data-sb-automation="$attrs['data-sb-automation'] || 'rb-container'"
    data-sb-regression="sb-radio-button"
    @click="inputChanged"
  >
    <i
      aria-hidden="true"
      :class="controlIndicator"
    />
    <input
      :id="internalId"
      data-sb-automation="radio-button-input"
      :checked="isChecked"
      type="radio"
      v-bind="$attrs"
    >
    <div class="label-wrapper">
      <!--
        @slot slot to show buttons to various pages
      -->
      <slot />
    </div>
  </label>
  <SbRadioButtonNext
    v-else
    :model="model"
    :value="value"
    :variation="variation"
    :disabled="disabled"
    @click.prevent="inputChanged"
  >
    <slot />
  </SbRadioButtonNext>
</template>

<script>
import isEqual from 'lodash/isEqual';
import uuid from '@src/utils/uuid';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';
import SbRadioButtonNext from '@src/components/RadioButton/SbRadioButtonNext.vue';

/**
 * Radio buttons allow the user to select one option from a set.
 */
export default {
  name: 'SbRadioButton',
  components: {
    SbRadioButtonNext,
  },
  mixins: [versionedComponentsMixin],
  // model: {
  //   prop: 'model',
  //   event: 'input',
  // },
  props: {
    /**
     * Prop that v-model's value is bound to. This enables you to use the radio-button with
     * the v-model="myModel" syntax.
     */
    model: {
      type: [String, Boolean, Object, Number],
      default: null,
    },
    /**
     * The value of the radio-button. When selected v-model="myModel" will equal the value of this prop.
     */
    value: {
      type: [String, Boolean, Object, Number],
      default: null,
    },
    /**
     * Style variation.
     * `primary | bigbtn`
     */
    variation: {
      type: String,
      default: 'primary',
      validator: (value) => value.match(/(primary|bigbtn)/),
    },
    /**
     * Id of the radio button. If not provided the default will be the components uuid.
     */
    id: {
      type: String,
      default: null,
    },
    /**
     * Disable radio button from user input
    */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    /**
     * Emitted with the new value of the radio
     */
    'update:model',
  ],
  data: () => ({
    internalId: null,
  }),
  computed: {
    isChecked() {
      if (this.$options.props.value) return isEqual(this.model, this.value);
      return this.model;
    },
    radioControlClass() {
      const variation = `sb-radio-button-${this.variation}`;
      const classes = ['sb-radio-button', variation];
      if (this.isChecked) classes.push('checked');
      if (this.disabled) classes.push('disabled');
      return classes;
    },
    controlIndicator() {
      const map = {
        checked: {
          primary: 'control-indicator',
          bigbtn: 'control-indicator control-indicator--custom-check sb-icon-check',
        },
        default: {
          primary: 'control-indicator',
          bigbtn: 'control-indicator',
        },
      };
      return map[this.isChecked ? 'checked' : 'default'][this.variation];
    },
  },
  mounted() {
    this.setId();
  },
  methods: {
    setId() {
      this.internalId = this.id || uuid();
    },
    inputChanged() {
      if (this.disabled) return;
      this.$emit('update:model', this.value);
    },
  },
};
</script>

<style scoped lang='scss'>
@import '@src/components/RadioButton/style/sb-radio-button.scss';
</style>
